import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import bankAccountService from '../../services/bankAccountService';
import { reportsService } from '../../services/reportsService';
import { formatCurrency } from '../../utils/formatters';
import { api } from '../../services/api';

const T = {
  bg: '#faf7ee',
  card: '#ffffff',
  elevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#d97706',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  textDim: '#e3dcc4',
  border: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  green: '#16a34a',
  blue: '#2563eb',
  red: '#dc2626',
  purple: '#7c3aed',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  background: '#f4efe2',
  border: `1px solid ${T.borderSubtle}`,
  borderRadius: 4,
  color: '#5b5a52',
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase' as const,
  color: T.textMuted,
  marginBottom: 8,
};

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: T.textMuted,
  background: T.elevated,
  borderBottom: `1px solid ${T.border}`,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: T.textSecondary,
  borderBottom: `1px solid ${T.border}`,
  whiteSpace: 'nowrap',
};

export const BankReports: React.FC = () => {
  const [selectedAccount, setSelectedAccount] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReport, setActiveReport] = useState<'statements' | 'cashflow' | 'analysis'>('statements');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const reports = [
    { id: 'statements', label: 'Account Statements', icon: '📋' },
    { id: 'cashflow', label: 'Cash Flow Analysis', icon: '💰' },
    { id: 'analysis', label: 'Transaction Analysis', icon: '📊' }
  ];

  // Load bank accounts list
  const { data: bankAccountsResponse, isLoading: accountsLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => bankAccountService.getBankAccounts({ isActive: true })
  });

  // Bank statement — real data from backend
  const { data: bankStatement, isLoading: statementLoading } = useQuery({
    queryKey: ['bank-statement', selectedAccount, dateRange.startDate, dateRange.endDate],
    queryFn: () => reportsService.getBankAccountStatement({
      accountId: selectedAccount ? (selectedAccount as any) : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    }),
  });

  // Cash flow analysis — real data from backend
  const { data: cashFlow, isLoading: cashFlowLoading } = useQuery({
    queryKey: ['cash-flow-report', dateRange.startDate, dateRange.endDate],
    queryFn: () => reportsService.getCashFlowAnalysis({
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    }),
  });

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      const endpointMap: Record<string, string> = {
        statements: '/reports/bank-statement',
        cashflow: '/reports/cash-flow',
        analysis: '/reports/analytics',
      };
      const endpoint = endpointMap[activeReport] ?? '/reports/bank-statement';
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = `${activeReport}.${ext}`;

      const params = new URLSearchParams({
        format,
        ...(selectedAccount && { accountId: selectedAccount }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate })
      });

      const response = await api.get(`${endpoint}?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const renderFilters = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
      <div>
        <label style={labelStyle}>Bank Account</label>
        <select
          value={selectedAccount}
          onChange={(e) => setSelectedAccount(e.target.value)}
          style={inputStyle}
          disabled={accountsLoading}
        >
          <option value="">All Accounts</option>
          {bankAccountsResponse?.accounts?.map((acc) => (
            <option key={acc.id} value={acc.id}>
              {acc.accountName} — {acc.bankName}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={labelStyle}>From Date</label>
        <input
          type="date"
          value={dateRange.startDate}
          onChange={(e) => setDateRange(p => ({ ...p, startDate: e.target.value }))}
          style={inputStyle}
        />
      </div>
      <div>
        <label style={labelStyle}>To Date</label>
        <input
          type="date"
          value={dateRange.endDate}
          onChange={(e) => setDateRange(p => ({ ...p, endDate: e.target.value }))}
          style={inputStyle}
        />
      </div>
    </div>
  );

  // ── Account Statements ────────────────────────────────────────────────────────
  const renderStatementsReport = () => {
    const summary = bankStatement?.summary;
    const accounts = (bankStatement?.accountBalances ?? []) as any[];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderFilters()}

        {/* Summary stats — real */}
        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
            Account Balance Summary
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {([
              { label: 'Total Accounts', value: String(summary?.totalAccounts ?? bankAccountsResponse?.accounts?.length ?? 0), color: T.blue },
              { label: 'Total Balance', value: formatCurrency(summary?.totalBalance ?? 0), color: T.green },
              { label: 'Total Inflows', value: formatCurrency(summary?.totalInflows ?? 0), color: T.green },
              { label: 'Total Outflows', value: formatCurrency(summary?.totalOutflows ?? 0), color: T.red },
            ] as { label: string; value: string; color: string }[]).map((stat) => (
              <div key={stat.label} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
                <div style={labelStyle}>{stat.label}</div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: stat.color, lineHeight: 1 }}>
                  {statementLoading ? '—' : stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Account list — real */}
        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}` }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, margin: 0 }}>
              Bank Account Details
            </h3>
          </div>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {statementLoading ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
            ) : accounts.length === 0 ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>No account data found.</div>
            ) : accounts.map((acc) => (
              <div key={acc.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, color: T.textPrimary, marginBottom: 4 }}>{acc.accountName}</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: T.textMuted }}>{acc.bankName} &bull; {acc.accountNumber}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: acc.balance >= 0 ? T.green : T.red }}>
                      {formatCurrency(acc.balance)}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: T.textMuted, marginTop: 2 }}>Current Balance</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 4 }}>Total Inflows</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: T.green }}>{formatCurrency(acc.totalInflows ?? 0)}</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 4 }}>Total Outflows</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: T.red }}>{formatCurrency(acc.totalOutflows ?? 0)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Cash Flow ─────────────────────────────────────────────────────────────────
  const renderCashFlowReport = () => {
    const summary = cashFlow?.summary;
    const monthly = (cashFlow?.monthlyData ?? []) as any[];
    const avgMonthly = monthly.length > 0 ? (summary?.netCashFlow ?? 0) / monthly.length : 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderFilters()}

        {/* Overview — real */}
        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
            Cash Flow Overview
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {([
              { label: 'Total Inflows', value: formatCurrency(summary?.totalInflows ?? 0), color: T.green },
              { label: 'Total Outflows', value: formatCurrency(summary?.totalOutflows ?? 0), color: T.red },
              { label: 'Net Cash Flow', value: formatCurrency(summary?.netCashFlow ?? 0), color: (summary?.netCashFlow ?? 0) >= 0 ? T.blue : T.red },
              { label: 'Avg Monthly Flow', value: formatCurrency(avgMonthly), color: T.purple },
            ] as { label: string; value: string; color: string }[]).map((stat) => (
              <div key={stat.label} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
                <div style={labelStyle}>{stat.label}</div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: stat.color, lineHeight: 1 }}>
                  {cashFlowLoading ? '—' : stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Monthly breakdown — real */}
          <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
              Monthly Cash Flow
            </h3>
            {cashFlowLoading ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Loading...</div>
            ) : monthly.length === 0 ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 24, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>No monthly data for the selected period.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {monthly.slice(-6).map((m, i) => (
                  <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 4, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, color: T.textPrimary }}>{m.month}</span>
                      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: m.netFlow >= 0 ? T.blue : T.red }}>
                        Net: {formatCurrency(m.netFlow)}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 4 }}>Inflows</div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: T.green }}>{formatCurrency(m.inflows)}</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 4 }}>Outflows</div>
                        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: T.red }}>{formatCurrency(m.outflows)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Account performance — real */}
          <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
              Account Performance
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {((bankStatement?.accountBalances as any[]) ?? bankAccountsResponse?.accounts ?? []).slice(0, 6).map((acc: any) => (
                <div key={acc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: T.card, borderRadius: 4, border: `1px solid ${T.border}` }}>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500, color: T.textPrimary }}>{acc.accountName}</div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: T.textMuted }}>{acc.bankName}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: acc.balance >= 0 ? T.green : T.red }}>
                      {formatCurrency(acc.balance)}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: T.textMuted }}>Balance</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Transaction Analysis ──────────────────────────────────────────────────────
  const renderAnalysisReport = () => {
    const transactions = (bankStatement?.transactions ?? []) as any[];
    const received = transactions.filter((t) => t.type === 'RECEIVED');
    const paid = transactions.filter((t) => t.type === 'PAID');
    const totalAmount = transactions.reduce((s, t) => s + (t.amount ?? 0), 0);
    const avgAmount = transactions.length > 0 ? totalAmount / transactions.length : 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renderFilters()}

        {/* Stats — real */}
        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, marginTop: 0, marginBottom: 16 }}>
            Transaction Analysis
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {([
              { label: 'Total Transactions', value: String(transactions.length), color: T.blue },
              { label: 'Average Amount', value: formatCurrency(avgAmount), color: T.textPrimary },
              { label: 'Inflow Transactions', value: String(received.length), color: T.green },
              { label: 'Outflow Transactions', value: String(paid.length), color: T.red },
            ] as { label: string; value: string; color: string }[]).map((stat) => (
              <div key={stat.label} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
                <div style={labelStyle}>{stat.label}</div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: stat.color, lineHeight: 1 }}>
                  {statementLoading ? '—' : stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transactions table — real */}
        <div style={{ background: T.elevated, border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}` }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, color: T.textPrimary, margin: 0 }}>
              Transactions
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date', 'Account', 'Description', 'Type', 'Amount', 'Reference'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statementLoading ? (
                  <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: T.textMuted, padding: 24 }}>Loading...</td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: T.textMuted, padding: 24 }}>No transactions found for the selected period.</td></tr>
                ) : transactions.map((t) => (
                  <tr key={t.id}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = T.elevated; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                  >
                    <td style={tdStyle}>{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                    <td style={tdStyle}>{t.bankAccount ?? '—'}</td>
                    <td style={{ ...tdStyle, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description ?? '—'}</td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex', padding: '2px 8px', borderRadius: 4,
                        fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 600,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        background: t.type === 'RECEIVED' ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
                        color: t.type === 'RECEIVED' ? T.green : T.red,
                      }}>
                        {t.type === 'RECEIVED' ? 'Inflow' : 'Outflow'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: t.type === 'RECEIVED' ? T.green : T.red }}>
                      {t.type === 'PAID' ? '−' : '+'}{formatCurrency(t.amount ?? 0)}
                    </td>
                    <td style={{ ...tdStyle, color: T.textMuted }}>{t.reference ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeReport) {
      case 'statements': return renderStatementsReport();
      case 'cashflow': return renderCashFlowReport();
      case 'analysis': return renderAnalysisReport();
      default: return renderStatementsReport();
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 700, color: T.textPrimary, margin: 0, letterSpacing: '0.02em' }}>
          Bank Reports
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => handleExport('excel')}
            onMouseEnter={() => setHoveredBtn('excel')}
            onMouseLeave={() => setHoveredBtn(null)}
            style={{ padding: '8px 16px', background: hoveredBtn === 'excel' ? 'rgba(22,163,74,0.15)' : 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 4, color: T.green, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background 0.2s' }}
          >Export Excel</button>
          <button
            onClick={() => handleExport('pdf')}
            onMouseEnter={() => setHoveredBtn('pdf')}
            onMouseLeave={() => setHoveredBtn(null)}
            style={{ padding: '8px 16px', background: hoveredBtn === 'pdf' ? 'rgba(220,38,38,0.15)' : 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 4, color: T.red, fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background 0.2s' }}
          >Export PDF</button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ borderBottom: `1px solid ${T.border}`, marginBottom: 24 }}>
        <nav style={{ display: 'flex', gap: 0 }}>
          {reports.map((report) => {
            const isActive = activeReport === report.id;
            const isHovered = hoveredTab === report.id;
            return (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id as any)}
                onMouseEnter={() => setHoveredTab(report.id)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  padding: '10px 20px', border: 'none',
                  borderBottom: isActive ? `2px solid ${T.gold}` : '2px solid transparent',
                  background: 'transparent', cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 600,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: isActive ? T.gold : isHovered ? T.textSecondary : T.textMuted,
                  whiteSpace: 'nowrap', transition: 'color 0.2s, border-color 0.2s',
                  marginBottom: -1, display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span style={{ fontSize: 13 }}>{report.icon}</span>
                {report.label}
              </button>
            );
          })}
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};
